/**
 * Tiered pricing RESOLUTION for the public bundle.
 *
 * The band TABLE no longer lives here. It moved to
 * `@brandbox/content-schema`'s `pricingBands.ts` (Chapter 10, P2a) because the
 * admin needs the same table to render the tier editor, and a second copy of a
 * price table is a pricing bug waiting for a deploy. `TIER_BANDS`, `Tier`,
 * `getActiveTier` and `describeBand` are re-exported here so this module stays
 * the public app's one pricing entry point — but they are the SAME objects,
 * asserted by `pricing.test.ts`.
 *
 * What stays here is `getTieredPrice`: the resolution rule against the public
 * app's own `Option` type.
 *
 * Chapter 10 (F4) added the optional `500-999` and `1000+` bands. No stored
 * document carries them, and the downward walk below resolves an unpriced band
 * from the highest priced band beneath it — so every seeded price is unchanged.
 * `pricing.seededParity.test.ts` freezes that as a golden master.
 *
 * VAT is NOT displayed anywhere. The configurator shows only a static
 * "Excl. VAT" label — no VAT *amount* is ever computed into the rendered total.
 * The `getVatPercent` / `computeVat` helpers read `defaults.vatPercent` and
 * exist for FUTURE use (a VAT line is a deliberate visual change); they are
 * intentionally kept + unit-tested so a later phase can wire them in without
 * re-deriving the maths. Do not assume `defaults.vatPercent` reaches the UI
 * today — it does not.
 */

import {
  TIER_BANDS,
  TIER_BAND_KEYS,
  describeBand,
  getActiveTier,
  getBand,
  hasEffectiveTierPrices,
  resolveTieredPrice,
  type Tier,
  type TierBand,
} from '@brandbox/content-schema';
import type { FlowConfig, Option, Selections } from '../types';

export {
  TIER_BANDS,
  TIER_BAND_KEYS,
  describeBand,
  getActiveTier,
  getBand,
  /**
   * "Does any band carry a non-zero price?" — the publish gate's own predicate
   * (P2a). Re-exported so the configurator's "price depends on quantity" note
   * asks exactly the question the gate asks, instead of testing the table's mere
   * presence (an all-zero table does NOT vary with quantity).
   */
  hasEffectiveTierPrices,
};
export type { Tier, TierBand };

/**
 * The per-unit price of an option at a given order quantity.
 *
 * Chapter 10 (P4b): the resolution walk moved to `@brandbox/content-schema`'s
 * `resolveTieredPrice`, following the band table it reads (which moved in P2a
 * for the same reason). The ADMIN renders the itemised order emails and cannot
 * import this module; a second copy of the walk over there would put the emailed
 * line price and the on-screen line price on separate implementations of one
 * rule. This is a re-export of the SAME function, asserted in `pricing.test.ts`
 * — the `Option` parameter type is the only thing this app adds.
 */
export const getTieredPrice: (option: Option, qty: number) => number =
  resolveTieredPrice;

/** Band keys, low to high — the order `getLowestBandPrice` walks. */
const TIERS: readonly Tier[] = TIER_BANDS.map((b) => b.key);

/**
 * The cheapest band an option is authored at — what a "from €X" label means.
 *
 * Chapter 10 (F4): the option card used to hard-code the `250+` band as its
 * "from" price and print the raw key beside it. That was accidentally true while
 * `250+` was the top band; the moment an operator prices `500-999` or `1000+`
 * lower, "from" would overstate the floor. It takes the MINIMUM over the priced
 * bands instead, and returns the band so the label can be resolved through
 * `describeBand`.
 *
 * `null` when no band is priced (a flat-priced option — the whole Office box).
 */
export function getLowestBandPrice(
  option: Option,
): { readonly price: number; readonly tier: Tier } | null {
  const tiers = option.tieredPrices;
  if (!tiers) return null;
  let best: { price: number; tier: Tier } | null = null;
  TIERS.forEach((tier) => {
    const price = tiers[tier];
    if (price === undefined || price === 0) return;
    if (best === null || price < best.price) best = { price, tier };
  });
  return best;
}

/**
 * The price of ONE box for the current selections, at a given order quantity.
 *
 * Extracted from `useConfigurator`'s pricing effect (Chapter 10, P2b) because
 * the quantity affordance has to answer "what would a box cost at 100?" using
 * the same arithmetic that produces the price the customer is looking at. A
 * second copy of this loop would be a second answer to that question.
 *
 * `minBoxes` is evaluated at the SAME hypothetical quantity, so a selection the
 * customer cannot have yet is not silently priced in.
 */
export function computeUnitBoxPrice(
  flow: FlowConfig,
  selections: Selections,
  qty: number,
): number {
  let unit = 0;
  flow.steps.forEach((step) => {
    (selections[step.id] ?? []).forEach((id) => {
      const option = step.options?.find((o) => o.id === id);
      if (!option) return;
      if (option.minBoxes && qty < option.minBoxes) return;
      unit += getTieredPrice(option, qty);
    });
  });
  return unit;
}

/** Read the VAT rate (percent) from the defaults surface; 0 when unset. */
export function getVatPercent(defaults: { vatPercent?: number }): number {
  return defaults.vatPercent && defaults.vatPercent > 0 ? defaults.vatPercent : 0;
}

/** Compute the VAT amount for a net total at the given percentage. */
export function computeVat(net: number, vatPercent: number): number {
  return (net * vatPercent) / 100;
}
